import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ProjectsToolbox } from '../components/projects/projectsToolbox';
import { AppMain } from '../layout/appMain';
import { Project as ProjectModel } from '../models/project';
import {
  GetProjectsParams,
  getProjects as getAllProjects,
} from '../services/api/projectService';
import { ProjectDialogAdd } from '../components/projects/projectDialogAdd';
import { Box } from '@mui/material';
import { AppNoFeedback } from '../components/app/appNoFeedback';
import { Project } from '../components/projects/project';
import { ProjectDialogEdit } from '../components/projects/projectDialogEdit';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Client } from '../models/client';
import { getClient } from '../services/api/clientServices';
import AddIcon from '@mui/icons-material/Add';
import { useSelector } from 'react-redux';
import { selectProfile } from '../store/profileSlice';
import useAppSnackbar from '../hooks/useAppSnackbar';
import AppLoading from '../components/app/appLoading';

export enum ViewMode {
  PROJECT = 'Project',
  TASK = 'Task',
}

interface RouterProp {
  client?: Client;
  projectsComingFromClient?: boolean;
}

const style = {
  box: {
    mt: 4,
    display: 'flex',
    flexWrap: 'wrap',
  },
} as const;

export function PageProjects(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();

  const state: RouterProp = location.state || {};
  const URLparams = useParams();

  const [projects, setProjects] = useState<ProjectModel[]>([]);
  const [isOpenAddProject, setIsOpenAddProject] = useState<boolean>(false);
  const [isOpenEditProject, setIsOpenEditProject] = useState<boolean>(false);
  const [projectToEdit, setProjectToEdit] = useState<ProjectModel | null>(null);
  const [projectQueryParams, setProjectQueryParams] =
    useState<Partial<GetProjectsParams> | null>(null);
  const [client, setClient] = useState<Client | null>(null);
  const [loadingClient, setLoadingClient] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const { onErrorSnackbar } = useAppSnackbar();

  const user = useSelector(selectProfile);

  useEffect(() => {
    if (URLparams.id) {
      getClient(URLparams.id)
        .then((response) => {
          setClient(response);
        })
        .catch((error) => onErrorSnackbar(error))
        .finally(() => {
          setLoadingClient(false);
        });

      return;
    }

    setLoadingClient(false);
  }, [URLparams.id, onErrorSnackbar]);

  const getProjects = useCallback(
    (params?) => {
      if (!user) return;

      setIsLoading(true);
      getAllProjects({ ...params, ...(client && { clientId: client?.id }) })
        .then((responseProjects) => {
          setProjects(responseProjects.items);
        })
        .catch((error) => onErrorSnackbar(error))
        .finally(() => setIsLoading(false));
    },
    [client, onErrorSnackbar, user]
  );

  useEffect(() => {
    if (loadingClient) return;
    getProjects();
  }, [getProjects, loadingClient]);

  const handleRequestSuccessful = useCallback(() => {
    getProjects();
  }, [getProjects]);

  const handleSearch = useCallback(
    (params?: Partial<GetProjectsParams>) => {
      getProjects(params);
      if (params) {
        setProjectQueryParams(params);
      }
    },
    [getProjects]
  );

  const handleReloadProjects = useCallback(() => {
    if (projectQueryParams) {
      handleSearch(projectQueryParams);
    } else {
      getProjects();
    }
  }, [projectQueryParams, handleSearch, getProjects]);

  const handleEditProject = useCallback((project) => {
    setIsOpenEditProject(true);
    setProjectToEdit(project);
  }, []);

  const handleOpenAddProjectDialog = useCallback(() => {
    setIsOpenAddProject(true);
  }, []);

  const handleCloseAddProjectDialog = useCallback(() => {
    setIsOpenAddProject(false);
  }, []);

  const handleCloseEditProjectDialog = useCallback(() => {
    setIsOpenEditProject(false);
  }, []);

  const onNavigateToClients = useCallback(() => {
    navigate('/clients');
  }, [navigate]);

  const headerButtons = useMemo(() => {
    return [{ icon: AddIcon, onClick: handleOpenAddProjectDialog }];
  }, [handleOpenAddProjectDialog]);

  return (
    <AppMain buttons={headerButtons}>
      <ProjectsToolbox
        onSwitch={onNavigateToClients}
        onSearch={handleSearch}
        onOpenAddProject={handleOpenAddProjectDialog}
        client={client}
        isFromClient={state.projectsComingFromClient}
        loadingClient={loadingClient}
      />

      {isLoading ? (
        <AppLoading />
      ) : (
        <Box sx={style.box}>
          {projects.length == 0 && <AppNoFeedback info="project" />}
          {projects.length !== 0 &&
            projects.map((project) => {
              return (
                <Project
                  project={project}
                  key={project.id}
                  handleEditProject={handleEditProject}
                />
              );
            })}
        </Box>
      )}

      <ProjectDialogAdd
        onCloseAddDialog={handleCloseAddProjectDialog}
        isOpen={isOpenAddProject}
        onRequestSuccessful={handleRequestSuccessful}
      />

      <ProjectDialogEdit
        isOpen={isOpenEditProject}
        onRequestSuccessful={handleReloadProjects}
        onCloseEditDialog={handleCloseEditProjectDialog}
        project={projectToEdit}
      />
    </AppMain>
  );
}
